import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Image as ImageIcon, 
  Upload, 
  Trash2, 
  Eye, 
  EyeOff, 
  GripVertical, 
  Tag, 
  Star,
  Check,
  X,
  Plus,
  Edit2,
  Filter,
  FolderPlus
} from "lucide-react";
import { toast } from "sonner";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import axios from "axios";
import { getBackendUrl } from "@/utils/apiBase";

const API = getBackendUrl();

const SortablePhotoCard = ({ photo, isSelected, onSelect, onToggleVisibility, onTogglePinned, onDelete, onReplace, onUpdateCategory, onUpdateSection, sections }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: photo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [editingCategory, setEditingCategory] = useState(false);
  const [category, setCategory] = useState(photo.category || "");
  const [editingSection, setEditingSection] = useState(false);
  const [section, setSection] = useState(photo.section || "");
  const replaceInputRef = useRef(null);

  const handleSaveCategory = () => {
    onUpdateCategory(photo.id, category);
    setEditingCategory(false);
  };

  const handleSaveSection = () => {
    onUpdateSection(photo.id, section);
    setEditingSection(false);
  };

  const handleReplaceClick = () => {
    replaceInputRef.current?.click();
  };

  const handleReplaceFile = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await onReplace(photo.id, file);
      e.target.value = ''; // Reset input
    }
  };

  const currentSection = sections.find(s => s.name === photo.section);

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      <Card className={`${photo.is_visible ? '' : 'border-gray-300'} ${isSelected ? 'ring-2' : ''} hover:shadow-xl transition-all`} style={{ borderColor: photo.is_visible ? "#e8d5c4" : undefined, boxShadow: isSelected ? "0 0 0 2px #b8977e" : undefined }}>
        <CardContent className="p-3">
          {/* Selection Checkbox */}
          <div className="absolute top-2 left-2 z-20">
            <Checkbox
              checked={isSelected}
              onCheckedChange={onSelect}
              className="bg-white border-2 data-[state=checked]:text-white"
              style={{ borderColor: "#b8977e" }}
            />
          </div>

          {/* Drag Handle */}
          <div
            {...attributes}
            {...listeners}
            className="absolute top-2 right-2 cursor-move bg-white/80 rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity z-10"
          >
            <GripVertical className="h-4 w-4 text-gray-600" />
          </div>

          {/* Pinned Star */}
          {photo.is_pinned && (
            <div className="absolute top-10 right-2 z-10">
              <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
            </div>
          )}

          {/* Image */}
          <div className="relative aspect-square mb-2 bg-gray-100 rounded-lg overflow-hidden mt-6">
            <img
              src={`${API}${photo.file_path}`}
              alt={photo.original_name}
              crossOrigin="anonymous"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23ddd" width="100" height="100"/><text x="50" y="50" text-anchor="middle" fill="%23999">?</text></svg>';
              }}
            />
            {!photo.is_visible && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <EyeOff className="h-8 w-8 text-white" />
              </div>
            )}
          </div>

          {/* Info */}
          <div className="space-y-2">
            <p className="text-xs font-medium truncate" title={photo.original_name}>
              {photo.original_name}
            </p>
            
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <span>{(photo.file_size / 1024).toFixed(0)} KB</span>
            </div>

            {/* Section Badge */}
            {editingSection ? (
              <div className="flex gap-1">
                <select
                  value={section}
                  onChange={(e) => setSection(e.target.value)}
                  className="h-6 text-xs border rounded px-1 flex-1"
                >
                  <option value="">Aucune</option>
                  {sections.map(s => (
                    <option key={s.id} value={s.name}>{s.label}</option>
                  ))}
                </select>
                <Button size="sm" onClick={handleSaveSection} className="h-6 px-2 text-xs">
                  <Check className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <div onClick={() => setEditingSection(true)} className="cursor-pointer">
                {currentSection ? (
                  <Badge variant="secondary" className="text-xs">
                    {currentSection.icon} {currentSection.label}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs text-gray-400">
                    Sans section
                  </Badge>
                )}
              </div>
            )}

            {/* Category */}
            {editingCategory ? (
              <div className="flex gap-1">
                <Input
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="Catégorie..."
                  className="h-6 text-xs"
                />
                <Button size="sm" onClick={handleSaveCategory} className="h-6 px-2 text-xs">
                  <Check className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <div onClick={() => setEditingCategory(true)} className="cursor-pointer">
                {photo.category ? (
                  <Badge variant="outline" className="text-xs">
                    <Tag className="h-3 w-3 mr-1" />
                    {photo.category}
                  </Badge>
                ) : (
                  <span className="text-xs text-gray-400">+ Catégorie</span>
                )}
              </div>
            )}

            {/* Quick Actions */}
            <div className="flex gap-1 pt-1">
              <Button
                size="sm"
                variant={photo.is_visible ? "default" : "outline"}
                className="flex-1 h-7 text-xs"
                onClick={() => onToggleVisibility(photo.id, photo.is_visible)}
                title={photo.is_visible ? "Masquer" : "Afficher"}
              >
                {photo.is_visible ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
              </Button>
              <Button
                size="sm"
                variant={photo.is_pinned ? "default" : "outline"}
                className="flex-1 h-7 text-xs"
                onClick={() => onTogglePinned(photo.id, photo.is_pinned)}
                title={photo.is_pinned ? "Désépingler" : "Épingler"}
              >
                <Star className={`h-3 w-3 ${photo.is_pinned ? 'fill-current' : ''}`} />
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2"
                onClick={handleReplaceClick}
                title="Remplacer l'image"
              >
                <Upload className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-red-600"
                onClick={() => onDelete(photo.id)}
                title="Supprimer"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
            
            {/* Hidden file input for replace */}
            <input
              ref={replaceInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleReplaceFile}
              className="hidden"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const PhotosManager = () => {
  const [photos, setPhotos] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [filterSection, setFilterSection] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [showSectionDialog, setShowSectionDialog] = useState(false);
  const [newSectionData, setNewSectionData] = useState({ name: "", label: "", icon: "" });
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchPhotos();
    fetchSections();
  }, []);

  const fetchPhotos = async () => {
    try {
      const { data } = await axios.get(`${API}/api/photos`);
      setPhotos(data);
    } catch (error) {
      console.error("Erreur chargement photos:", error);
      toast.error("Erreur lors du chargement");
    }
  };

  const fetchSections = async () => {
    try {
      const { data } = await axios.get(`${API}/api/photo-sections`);
      setSections(data);
    } catch (error) {
      console.error("Erreur chargement sections:", error);
    }
  };

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;

    if (photos.length + files.length > 50) {
      toast.error("Limite de 50 photos atteinte");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    
    Array.from(files).forEach(file => {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        toast.error(`${file.name}: Type non autorisé`);
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: Trop volumineux (max 10 MB)`);
        return;
      }
      formData.append('files', file);
    });

    try {
      const { data } = await axios.post(`${API}/api/photos/upload`, formData);
      toast.success(data.message || "Photos téléchargées");
      fetchPhotos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur upload");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedPhotos(filteredPhotos.map(p => p.id));
    } else {
      setSelectedPhotos([]);
    }
  };

  const handleBulkAction = async (action, section = null) => {
    if (selectedPhotos.length === 0) {
      toast.error("Aucune photo sélectionnée");
      return;
    }

    try {
      const { data } = await axios.post(`${API}/api/photos/bulk-action`, {
        photo_ids: selectedPhotos,
        action,
        section,
      });
      toast.success(data.message || "Action effectuée");
      fetchPhotos();
      setSelectedPhotos([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur action groupée");
    }
  };

  const handleToggleVisibility = async (photoId, currentVisibility) => {
    try {
      await axios.patch(`${API}/api/photos/${photoId}`, { is_visible: !currentVisibility });
      toast.success("Visibilité mise à jour");
      fetchPhotos();
    } catch (error) {
      toast.error("Erreur mise à jour");
    }
  };

  const handleTogglePinned = async (photoId, currentPinned) => {
    try {
      await axios.patch(`${API}/api/photos/${photoId}`, { is_pinned: !currentPinned });
      toast.success(currentPinned ? "Désépinglée" : "Épinglée ⭐");
      fetchPhotos();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleUpdateCategory = async (photoId, category) => {
    try {
      await axios.patch(`${API}/api/photos/${photoId}`, { category: category || null });
      toast.success("Catégorie mise à jour");
      fetchPhotos();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleUpdateSection = async (photoId, section) => {
    try {
      await axios.patch(`${API}/api/photos/${photoId}`, { section: section || null });
      toast.success("Section mise à jour");
      fetchPhotos();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm("Supprimer cette photo ?")) return;
    try {
      await axios.delete(`${API}/api/photos/${photoId}`);
      toast.success("Photo supprimée");
      fetchPhotos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur suppression");
    }
  };

  const handleReplace = async (photoId, file) => {
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error("Type de fichier non autorisé (JPG, PNG, WEBP uniquement)");
      return;
    }

    // Validate file size (10 MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 MB)");
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await axios.post(`${API}/api/photos/${photoId}/replace`, formData);
      toast.success(data.message || "Photo remplacée");
      fetchPhotos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur remplacement");
    }
  };

  const handleCreateSection = async () => {
    if (!newSectionData.name || !newSectionData.label) {
      toast.error("Nom et label requis");
      return;
    }

    try {
      await axios.post(`${API}/api/photo-sections`, newSectionData);
      toast.success("Section créée");
      fetchSections();
      setShowSectionDialog(false);
      setNewSectionData({ name: "", label: "", icon: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur création");
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = filteredPhotos.findIndex(p => p.id === active.id);
    const newIndex = filteredPhotos.findIndex(p => p.id === over.id);

    const newPhotos = arrayMove(filteredPhotos, oldIndex, newIndex);
    
    // Update order
    const photoOrders = newPhotos.map((photo, index) => ({
      id: photo.id,
      order: index
    }));

    try {
      await axios.post(`${API}/api/photos/reorder`, photoOrders);
      toast.success("Ordre mis à jour");
      fetchPhotos();
    } catch (error) {
      toast.error("Erreur réorganisation");
    }
  };

  const filteredPhotos = filterSection === "all" 
    ? photos 
    : photos.filter(p => p.section === filterSection);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-['Playfair_Display'] font-bold flex items-center gap-2" style={{ color: "#2d2319" }}>
            <ImageIcon className="h-6 w-6 sm:h-8 sm:w-8" style={{ color: "#b8977e" }} />
            Gestion des Photos
          </h2>
          <p className="text-sm mt-1" style={{ color: "#5a5a5a" }}>
            {photos.length}/50 photos • {selectedPhotos.length} sélectionnée(s)
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowSectionDialog(true)}
            variant="outline"
            className="w-full sm:w-auto"
            style={{ borderColor: "#b8977e", color: "#b8977e", background: "#fff" }}
          >
            <FolderPlus className="mr-2 h-4 w-4" />
            Nouvelle Section
          </Button>
          <Button
            onClick={() => fileInputRef.current?.click()}
            className="w-full sm:w-auto text-white"
            disabled={uploading || photos.length >= 50}
            style={{ background: "#b8977e" }}
            onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.background = "#a68770"; }}
            onMouseLeave={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.background = "#b8977e"; }}
          >
            <Upload className="mr-2 h-4 w-4" />
            {uploading ? "Upload..." : "Télécharger"}
          </Button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => handleFileSelect(e.target.files)}
          className="hidden"
        />
      </div>

      {/* Bulk Actions Bar */}
      {selectedPhotos.length > 0 && (
        <Card style={{ background: "#faf8f5", borderColor: "#e8d5c4" }}>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-sm font-medium" style={{ color: "#2d2319" }}>{selectedPhotos.length} photo(s) :</span>
              <Button size="sm" onClick={() => handleBulkAction('show')}>
                <Eye className="h-3 w-3 mr-1" /> Afficher
              </Button>
              <Button size="sm" onClick={() => handleBulkAction('hide')}>
                <EyeOff className="h-3 w-3 mr-1" /> Masquer
              </Button>
              <Button size="sm" onClick={() => handleBulkAction('pin')}>
                <Star className="h-3 w-3 mr-1" /> Épingler
              </Button>
              <Button size="sm" onClick={() => handleBulkAction('unpin')}>
                <Star className="h-3 w-3 mr-1" /> Désépingler
              </Button>
              <select
                onChange={(e) => e.target.value && handleBulkAction('assign_section', e.target.value)}
                className="h-8 text-sm border rounded px-2"
              >
                <option value="">Assigner à...</option>
                {sections.map(s => (
                  <option key={s.id} value={s.name}>{s.label}</option>
                ))}
              </select>
              <Button size="sm" variant="destructive" onClick={() => handleBulkAction('delete')}>
                <Trash2 className="h-3 w-3 mr-1" /> Supprimer
              </Button>
              <Button size="sm" variant="outline" onClick={() => setSelectedPhotos([])}>
                <X className="h-3 w-3" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter Bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4" style={{ color: "#b8977e" }} />
        <Button
          size="sm"
          variant={filterSection === "all" ? "default" : "outline"}
          onClick={() => setFilterSection("all")}
          style={filterSection === "all"
            ? { background: "#b8977e", color: "#fff", borderColor: "#b8977e" }
            : { background: "#fff", color: "#b8977e", borderColor: "#e8d5c4" }}
        >
          Toutes ({photos.length})
        </Button>
        {sections.map((section) => {
          const count = photos.filter(p => p.section === section.name).length;
          const active = filterSection === section.name;
          return (
            <Button
              key={section.id}
              size="sm"
              variant={active ? "default" : "outline"}
              onClick={() => setFilterSection(section.name)}
              style={active
                ? { background: "#b8977e", color: "#fff", borderColor: "#b8977e" }
                : { background: "#fff", color: "#b8977e", borderColor: "#e8d5c4" }}
            >
              {section.icon} {section.label} ({count})
            </Button>
          );
        })}
      </div>

      {/* Drop Zone */}
      <div
        className="border-2 border-dashed rounded-xl p-6 text-center transition-colors"
        style={dragActive
          ? { borderColor: "#b8977e", background: "#fff9f5" }
          : { borderColor: "#e8d5c4", background: "#faf8f5" }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <Upload className="h-10 w-10 mx-auto mb-2" style={{ color: "#b8977e" }} />
        <p className="text-sm" style={{ color: "#5a5a5a" }}>Glissez-déposez ou cliquez sur Télécharger</p>
        <p className="text-xs mt-1" style={{ color: "#999" }}>JPG, PNG, WEBP • Max 10 MB • Max 50 photos</p>
      </div>

      {/* Select All */}
      {filteredPhotos.length > 0 && (
        <div className="flex items-center gap-2">
          <Checkbox
            checked={selectedPhotos.length === filteredPhotos.length}
            onCheckedChange={handleSelectAll}
          />
          <span className="text-sm text-gray-600">Tout sélectionner ({filteredPhotos.length})</span>
        </div>
      )}

      {/* Photos Grid */}
      {filteredPhotos.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={filteredPhotos.map(p => p.id)}
            strategy={rectSortingStrategy}
          >
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {filteredPhotos.map((photo) => (
                <SortablePhotoCard
                  key={photo.id}
                  photo={photo}
                  isSelected={selectedPhotos.includes(photo.id)}
                  onSelect={() => {
                    setSelectedPhotos(prev =>
                      prev.includes(photo.id)
                        ? prev.filter(id => id !== photo.id)
                        : [...prev, photo.id]
                    );
                  }}
                  onToggleVisibility={handleToggleVisibility}
                  onTogglePinned={handleTogglePinned}
                  onDelete={handleDelete}
                  onReplace={handleReplace}
                  onUpdateCategory={handleUpdateCategory}
                  onUpdateSection={handleUpdateSection}
                  sections={sections}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <ImageIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {filterSection === "all" ? "Aucune photo" : "Aucune photo dans cette section"}
            </p>
          </CardContent>
        </Card>
      )}

      {/* New Section Dialog */}
      <Dialog open={showSectionDialog} onOpenChange={setShowSectionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Créer une Nouvelle Section</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom (identifiant)</Label>
              <Input
                value={newSectionData.name}
                onChange={(e) => setNewSectionData({ ...newSectionData, name: e.target.value })}
                placeholder="ex: logos-clients"
              />
            </div>
            <div>
              <Label>Label (affichage)</Label>
              <Input
                value={newSectionData.label}
                onChange={(e) => setNewSectionData({ ...newSectionData, label: e.target.value })}
                placeholder="ex: Logos Clients"
              />
            </div>
            <div>
              <Label>Icône (emoji)</Label>
              <Input
                value={newSectionData.icon}
                onChange={(e) => setNewSectionData({ ...newSectionData, icon: e.target.value })}
                placeholder="ex: 🏢"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSectionDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateSection} style={{ background: "#b8977e", color: "#fff" }}>
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
